/**
 * Permission checking composable
 * Centralized permission logic for role-based access control
 */

import { computed } from 'vue'
import { getDefaultPermissionsByRole } from '@/constants/users'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/composables/useToast'

export function usePermissions() {
  const authStore = useAuthStore()
  const { error: showError } = useToast()

  // Get user's permissions
  const userPermissions = computed(() => {
    const explicitPermissions = Array.isArray(authStore.userProfile?.permissions)
      ? authStore.userProfile.permissions.filter((permission) => typeof permission === 'string' && permission.trim().length > 0)
      : []

    if (explicitPermissions.length > 0) {
      return explicitPermissions
    }

    return getDefaultPermissionsByRole(userRole.value)
  })

  // Get user's role
  const userRole = computed(() => {
    return authStore.userProfile?.role?.toLowerCase() || 'user'
  })

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission to check (e.g., 'projects:write')
   * @returns {boolean}
   */
  const hasPermission: (permission: string) => boolean = (permission) => {
    if (!authStore.isAuthenticated) {
      return false
    }

    // Mirror backend getPermissionsForRole: unknown role -> user matrix
    return userPermissions.value.includes(permission)
  }

  /**
   * Check if user can promote others to Super Admin
   * @returns {boolean}
   */
  const canPromoteToSuperAdmin = () => {
    if (!authStore.isAuthenticated) {
      return false
    }
    
    return userRole.value === 'super_admin'
  }

  /**
   * Check if user has ANY of the specified permissions
   * @param {string[]} permissions - Array of permissions to check
   * @returns {boolean}
   */
  const hasAnyPermission = (permissions) => {
    return permissions.some(permission => hasPermission(permission))
  }

  /**
   * Check if user has ALL of the specified permissions
   * @param {string[]} permissions - Array of permissions to check
   * @returns {boolean}
   */
  const hasAllPermissions = (permissions) => {
    return permissions.every(permission => hasPermission(permission))
  }

  /**
   * Check permission and show error if denied
   * @param {string} permission - Permission to check
   * @param {string} customMessage - Custom error message
   * @returns {boolean}
   */
  const requirePermission = (permission, customMessage = null) => {
    if (!hasPermission(permission)) {
      const message = customMessage || `You don't have permission to perform this action. Required: ${permission}`
      showError(message)
      return false
    }
    return true
  }


  /**
   * True when the user has role=owner and is a member of the given team.
   * Used to grant team CRUD to owners who are members.
   */
  const isOwnerMemberOf = (team?: { members?: string[] } | null): boolean => {
    if (!team || userRole.value !== 'owner') return false
    const userId = authStore.userProfile?.id
    if (!userId) return false
    return Array.isArray(team.members) && team.members.includes(userId)
  }

  /**
   * True when the user has role=owner and is a member of the given project.
   * Used to gate project CRUD: an owner can edit/delete a project only if
   * they are listed in its members. project.members may be raw string IDs
   * (write paths) or hydrated `{ id, name }` objects (list endpoint), so
   * accept both shapes.
   */
  const isOwnerMemberOfProject = (
    project?: { members?: Array<string | { id?: string }> } | null,
  ): boolean => {
    if (!project || userRole.value !== 'owner') return false
    const userId = authStore.userProfile?.id
    if (!userId || !Array.isArray(project.members)) return false
    return project.members.some((m) =>
      typeof m === 'string' ? m === userId : m?.id === userId,
    )
  }

  /**
   * Role-based permission checks
   */
  const can = {
    // Users
    // viewUsers gates the User Management page (sidebar + /users route).
    // Backed by users:manage so the regular `user` role can't reach it,
    // while still keeping users:read for id/batch-fetch lookups elsewhere
    // (assignee names, "created by" labels, member dropdowns, etc.).
    viewUsers: () => hasPermission('users:manage'),
    createUsers: () => hasPermission('users:write'),
    editUsers: () => hasPermission('users:write'),
    deleteUsers: () => hasPermission('users:delete'),
    manageUserRoles: () => hasPermission('users:manage_roles'),
    promoteToSuperAdmin: () => canPromoteToSuperAdmin(),
    viewKaizenFeedback: () => userRole.value === 'super_admin',
    
    // Teams
    viewTeams: () => hasPermission('teams:read'),
    createTeams: () => hasPermission('teams:write'),
    // editTeams/deleteTeams accept an optional team: role=owner members of the
    // team get CRUD on it, even though only super_admin has teams:write globally.
    editTeams: (team?: { members?: string[] }) =>
      hasPermission('teams:write') || isOwnerMemberOf(team),
    deleteTeams: (team?: { members?: string[] }) =>
      hasPermission('teams:delete') || isOwnerMemberOf(team),
    
    // Projects
    viewProjects: () => hasPermission('projects:read'),
    createProjects: () => hasPermission('projects:write'),
    // editProjects/deleteProjects accept an optional project: super_admin gets
    // CRUD on every project (via projects:write/delete), owners only on
    // projects they are a member of. When no project is passed (e.g. a
    // toolbar-level capability check), fall back to the global permission so
    // we don't break entry points that haven't been updated yet.
    editProjects: (project?: { members?: Array<string | { id?: string }> }) =>
      project
        ? userRole.value === 'super_admin' || isOwnerMemberOfProject(project)
        : hasPermission('projects:write'),
    deleteProjects: (project?: { members?: Array<string | { id?: string }> }) =>
      project
        ? userRole.value === 'super_admin' || isOwnerMemberOfProject(project)
        : hasPermission('projects:delete'),
    createPrivateProjects: () => hasPermission('projects:create_private'),

    // Cross Team Sanity (CTS)
    // Suite-level mutations (edit metadata, delete, manage teams in the
    // suite, add/remove test runs to/from the suite) are restricted to the
    // CTS creator. super_admin bypasses. Anyone authenticated can create
    // a CTS suite — there is no createCrossTeamSanity gate.
    // NOTE: This does NOT control the per-test-run "Run / Edit" buttons
    // INSIDE a CTS — those continue to use editTestRuns + project membership.
    editCrossTeamSanity: (cts?: { created_by?: string } | null): boolean => {
      if (userRole.value === 'super_admin') return true
      if (!cts) return false
      const userId = authStore.userProfile?.id
      return !!userId && cts.created_by === userId
    },

    // Test Cases
    viewTestCases: () => hasPermission('test_cases:read'),
    createTestCases: () => hasPermission('test_cases:write'),
    editTestCases: () => hasPermission('test_cases:write'),
    deleteTestCases: () => hasPermission('test_cases:delete'),
    
    // Test Runs
    viewTestRuns: () => hasPermission('test_runs:read'),
    createTestRuns: () => hasPermission('test_runs:write'),
    editTestRuns: () => hasPermission('test_runs:write'),
    executeTestRuns: () => hasPermission('test_runs:execute'),
    deleteTestRuns: () => hasPermission('test_runs:delete'),
    
    // Bugs
    viewBugs: () => hasPermission('bugs:read'),
    createBugs: () => hasPermission('bugs:write'),
    editBugs: () => hasPermission('bugs:write'),
    closeBugs: () => hasPermission('bugs:close'),
    deleteBugs: () => hasPermission('bugs:delete'),
    
    // Reports
    viewReports: () => hasPermission('reports:read'),
    createReports: () => hasPermission('reports:write'),
    editReports: () => hasPermission('reports:write'),
    deleteReports: () => hasPermission('reports:delete'),

    // Folders
    viewFolders: () => hasPermission('folders:read'),
    createFolders: () => hasPermission('folders:write'),
    editFolders: () => hasPermission('folders:write'),
    deleteFolders: () => hasPermission('folders:delete'),
    
    // Features
    viewFeatures: () => hasPermission('features:read'),
    createFeatures: () => hasPermission('features:create'),
    editFeatures: () => hasPermission('features:write'),
    deleteFeatures: () => hasPermission('features:delete'),
    
    // Comments
    viewComments: () => hasPermission('comments:read'),
    createComments: () => hasPermission('comments:write'),
    editComments: () => hasPermission('comments:write'),
    deleteComments: () => hasPermission('comments:delete'),
    
    // Settings
    viewSettings: () => hasPermission('settings:read'),
    editSettings: () => hasPermission('settings:write'),
  }

  /**
   * Check if user is a specific role
   */
  const isRole = {
    superAdmin: computed(() => userRole.value === 'super_admin'),
    owner: computed(() => userRole.value === 'owner'),
    user: computed(() => userRole.value === 'user'),
  }

  return {
    // Core permission checking
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    requirePermission,
    canPromoteToSuperAdmin,
    
    // Convenience methods
    can,
    isRole,
    
    // State
    userPermissions,
    userRole,
  }
}
